"use client"

import React from "react"
import { Copy, Mail } from "lucide-react"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { copyTextToClipboard } from "@/utils/clipboard"
import { buildMailtoHref, openEmailCompose } from "@/utils/email-helpers"
import { useTranslation } from "@/hooks/use-translation"
import { useToast } from "@/hooks/use-toast"

interface EmailCellProps {
  /** Already-resolved display text for the cell (never an object). */
  value: string
  /** True when inline editing is permitted for this cell. */
  isEditable: boolean
  /** Enter inline edit mode. Only wired when isEditable. */
  onEdit?: () => void
  /** Optional sibling-column badge, rendered exactly as the other cell renderers do. */
  badgeLabel?: string | null
}

/**
 * Grid cell for email columns: compose + copy, without changing how any other column behaves.
 *
 * Click semantics are split by editability on purpose, and inline-edit semantics are left ALONE:
 *
 *   • Read-only cell — the address itself is the mailto link. There is no edit action to compete
 *     with, so the most direct affordance is safe.
 *   • Editable cell — a usable address is styled like the read-only link, but it is NOT one: the cell
 *     body keeps its existing single-click-to-edit, and anything that is not a usable address stays
 *     plain, matching the read-only variant.
 *     Compose moves to a mail button that appears on hover. Making the address a link here would
 *     mean one click doing two different things, and switching the cell to double-click-to-edit would
 *     make email the only column in the grid that edits differently from the rest — the kind of
 *     inconsistency that surfaces as "the field won't open".
 *
 * Right-click offers Copy (and Send when the address is usable), on every email cell including empty
 * ones so the column behaves the same throughout. It is mounted only on email cells, so every other
 * cell keeps the native browser context menu.
 */
export function EmailCell({ value, isEditable, onEdit, badgeLabel }: EmailCellProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const text = (value ?? "").trim()
  // null for a malformed or empty address. No email field ships a validation regex, so historical
  // rows genuinely can hold non-addresses — those render as plain text with no dead compose action.
  const mailtoHref = buildMailtoHref(text)

  // Link styling is gated on the SAME condition the read-only branch uses, so the two variants agree.
  // Colouring unconditionally made a non-address ("n/a", legacy free text) read as a link in editable
  // columns while the read-only variant left it plain — and that is the one case where blue-underline
  // actively misleads, since those values get no compose action either.
  const textClass = mailtoHref
    ? "truncate flex-1 text-blue-600 group-hover:underline"
    : "truncate flex-1"

  const handleCopy = async () => {
    if (!text) return
    const ok = await copyTextToClipboard(text)
    // Toast, not in-menu state: Radix unmounts ContextMenuContent on onSelect (unless the handler
    // preventDefaults), so a tick rendered inside the menu is never on screen — which is what made
    // this different from the VMS panels the pattern came from, where the button stays mounted.
    //
    // Reporting the FAILURE is the part that matters: copyTextToClipboard returns false when both the
    // async API and the execCommand fallback fail (Chrome over HTTP), and silence there is
    // indistinguishable from success — the user walks away with an empty clipboard.
    toast(ok
      ? { title: t('copied') }
      : { variant: "destructive", title: t('copyFailed') })
  }

  const badge = badgeLabel ? (
    <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      {badgeLabel}
    </span>
  ) : null

  const body = isEditable ? (
    // Click handler belongs on the DIV, not the inner span — the generic text branch this replaces
    // put it here too. On the span it breaks the empty case outright: `truncate` blockifies the span
    // as a flex item, and an empty block is zero pixels tall, so a blank cell would have no clickable
    // area at all. A blank email cell is the commonest one to want to edit.
    <div
      className="group flex items-center gap-1 text-left px-2 py-1 rounded cursor-pointer hover:bg-gray-50"
      onClick={onEdit}
      title={t('clickToEdit')}
    >
      {/* Styled like the read-only variant's link — blue, underline on hover — so a usable address reads
          as an email in every column instead of looking like plain text in the editable ones. It is a
          SPAN, not an anchor: click still opens the editor, matching every other editable cell in the
          grid. Compose stays on the hover mail button and the right-click menu, both of which
          stopPropagation. A real anchor here would put two different actions on one click and, once the
          address fills the cell, leave nowhere to click to edit. */}
      <span className={textClass}>{text}</span>
      {badge}
      {mailtoHref && (
        <button
          type="button"
          // stopPropagation so composing never also opens the editor underneath.
          onClick={(e) => { e.stopPropagation(); openEmailCompose(text) }}
          // Hidden until hover/focus to keep the cell visually identical at rest, but focus-visible
          // and always in the tab order so it is reachable without a pointer.
          className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-gray-500 hover:text-blue-600 transition-opacity"
          title={t('sendEmailTo', { email: text })}
          aria-label={t('sendEmailTo', { email: text })}
        >
          <Mail className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  ) : (
    <div className="flex items-center gap-1 text-left px-2 py-1" title={text}>
      {mailtoHref ? (
        <a
          href={mailtoHref}
          className="truncate text-blue-600 hover:underline"
          title={t('sendEmailTo', { email: text })}
        >
          {text}
        </a>
      ) : (
        <span className="truncate">{text}</span>
      )}
      {badge}
    </div>
  )

  // Menu is mounted on EVERY email cell, including empty ones, so right-click behaves consistently
  // across the column instead of silently falling back to the browser menu on blanks. Copy is
  // disabled rather than hidden when there is nothing to copy — a menu whose items move between
  // rows is harder to use than one with a greyed entry.
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{body}</ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem onSelect={handleCopy} disabled={!text}>
          <Copy className="mr-2 h-4 w-4" />
          {t('copy')}
        </ContextMenuItem>
        {mailtoHref && (
          <ContextMenuItem onSelect={() => openEmailCompose(text)}>
            <Mail className="mr-2 h-4 w-4" />
            {t('sendEmail')}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}
